// 필드 대시보드 — 로봇 운영 이슈(2026-10-27 오너 "대시보드에 (이슈 정리 세션)처럼 정리한 것도 추가해야").
//
// 쿤스가 매달 상반기(1~15일) · 하반기(16일~말일)마다 메일로 돌리던 '로봇 운영 이슈 리포트'와 같은 순서 · 같은 말로
// 고장 조치 이력을 센다(계산은 서버 field-issue-summary.ts). 리포트 순서: 15일 단위 추이 그래프 → 요약 표 →
// 신규 이슈 · 주요 누적 이슈 → 조치 현황 → 건별 표(로봇 이슈 / 로봇 외 이슈 → 모델별). 기간은 '10월 상반기'처럼 사람이 부르는 이름으로 쓴다
// (오너 — "보름이라고는 사람은 그렇게 말 안해").

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ISSUE_CATEGORY_LABELS,
  kstYmd,
  REMEDIATION_STATUS_LABELS,
  type FieldIssueNoteSection,
  type FieldIssueSummaryResponse,
  type IssueCategory,
  type IssueCategoryCount,
  type IssueHalfMonthCount,
  type IssueModelGroup,
  type IssueTrendPoint,
  type RemediationStatus,
} from '@bhops/shared';
import { extractApiError } from '@/api/client';
import { teamDashboardApi } from '@/api/team-dashboard';
import { Button, Textarea } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import { canEditAny } from '@/permissions';
import { useAuthStore } from '@/stores/auth';
import { pill, REMEDIATION_ORDER, REMEDIATION_TONE } from '@/pages/field/field-tones';
import { HorizontalBars, Section, SHARED_EMPTY_HINT, SkeletonBox } from './_shared';

// 표는 모두 data-plain-table — 칸마다 선이 있어 머리글 칸 사이 초록 선(globals.css)을 긋지 않는다(2026-10-27 오너
// "헤더에 이 초록선 없애라고"). 끌어서 열 폭 바꾸기는 그대로 된다.

/** 리포트 그래프와 같은 색 — 전체는 짙은 남색, 로봇 이슈는 파랑, 로봇 외 이슈는 주황. 모델은 파랑 · 빨강 · 보라. */
const TOTAL_COLOR = '#1f2a44';
const ROBOT_COLOR = '#2f5d9e';
const NON_ROBOT_COLOR = '#e8963a';
/**
 * 글씨 색 — '로봇 이슈'와 '로봇 외 이슈'가 '외' 한 글자 차이라 색으로도 가른다(2026-10-28 오너 "로봇 외라는 글씨가
 * 잘 안보이니 색을 바꿔서 구분하자"). 그래프 주황은 흰 바탕 글씨로는 옅어서 글씨는 더 짙은 주황.
 */
const NON_ROBOT_TEXT = '#c2410c';
const CATEGORY_TEXT: Record<IssueCategory, string> = {
  ROBOT: ROBOT_COLOR,
  NON_ROBOT: NON_ROBOT_TEXT,
};
const MODEL_COLOR: Record<IssueModelGroup, string> = {
  GEN1: '#2563eb',
  GEN2: '#dc2626',
  ZEPHYR: '#7c3aed',
  기타: '#888888',
};
const CHART_MODELS = ['GEN1', 'GEN2', 'ZEPHYR'] as const;

export function FieldIssueSummarySection() {
  const navigate = useNavigate();
  // undefined 면 서버가 다 끝난 가장 최근 반기(오늘이 10월 하반기면 10월 상반기)를 고른다.
  const [period, setPeriod] = useState<string | undefined>(undefined);
  // 고른 선('total', 'GEN1.robot' …) — 그래프 셋과 요약 표가 함께 쓴다(2026-10-28 오너 "그래프 선택하면 요약 표에서도
  // 해당하는 내용에 하이라이트"). 고른 선이 있는 그래프나 요약 표 밖을 누르면 풀린다.
  const [lineFocus, setLineFocus] = useState<string | null>(null);
  useEffect(() => {
    if (!lineFocus) return;
    const onDown = (e: PointerEvent) => {
      const el = e.target as Element | null;
      const chart = el?.closest?.('[data-trend-lines]');
      if (chart?.getAttribute('data-trend-lines')?.split(' ').includes(lineFocus)) return;
      if (el?.closest?.('[data-keep-line-focus]')) return;
      setLineFocus(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [lineFocus]);
  const q = useQuery({
    queryKey: ['dashboard', 'field', 'issue-summary', period ?? 'default'],
    queryFn: () => teamDashboardApi.fieldIssueSummary(period),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const d = q.data;

  const picker = d && (
    <select
      aria-label="기간 선택"
      value={d.period.key}
      onChange={(e) => setPeriod(e.target.value)}
      style={selectStyle}
      data-testid="issue-summary-period"
    >
      {[...d.trend].reverse().map((t) => (
        <option key={t.key} value={t.key}>
          {`${t.key.slice(0, 4)}년 ${t.label} (${t.total}건)`}
        </option>
      ))}
    </select>
  );
  // '26.10 상반기 조치 이력 보기' — 대시보드가 센 그 반기 줄만 걸러 연다(2026-10-28 오너).
  const listLink = d && (
    <Link
      to={`/field/fault-remediations?issueHalf=${d.period.key}`}
      style={{ fontSize: 12, color: 'var(--fg-brand)', textDecoration: 'none' }}
    >
      {shortHalf(d.period.key)} 조치 이력 보기 →
    </Link>
  );

  return (
    <Section title="로봇 운영 이슈" titleExtra={picker} extra={listLink}>
      {q.isLoading || !d ? (
        q.isError ? (
          <div style={SHARED_EMPTY_HINT}>로봇 운영 이슈를 불러오지 못했습니다.</div>
        ) : (
          <SkeletonBox style={{ height: 240 }} />
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
            {d.period.range} 사이에 생긴 이슈를 {d.prev.label}, {d.sameHalfLastMonth.label}와
            비교합니다.
            {d.period.ongoing ? ' 아직 끝나지 않은 기간이라 숫자가 더 늘 수 있습니다.' : ''} 통합
            캘린더의 정기 유지보수는 세지 않습니다.
          </div>

          <HeadlineTiles d={d} />

          <ChartPanel
            title="15일 단위 전체 이슈 발생 추이"
            trend={d.trend}
            height={220}
            lines={[
              { key: 'total', name: '전체', color: TOTAL_COLOR },
              { key: 'robot', name: ISSUE_CATEGORY_LABELS.ROBOT, color: ROBOT_COLOR },
              { key: 'nonRobot', name: ISSUE_CATEGORY_LABELS.NON_ROBOT, color: NON_ROBOT_COLOR },
            ]}
            periodKey={d.period.key}
            onSelect={setPeriod}
            focus={lineFocus}
            onFocus={setLineFocus}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {(['robot', 'nonRobot'] as const).map((cat) => (
              <ChartPanel
                key={cat}
                style={{ flex: '1 1 360px', minWidth: 0 }}
                title={`${ISSUE_CATEGORY_LABELS[cat === 'robot' ? 'ROBOT' : 'NON_ROBOT']} 15일 단위 모델별 추이`}
                trend={d.trend}
                height={180}
                lines={CHART_MODELS.map((m) => ({
                  key: `${m}.${cat}`,
                  name: m,
                  color: MODEL_COLOR[m],
                }))}
                periodKey={d.period.key}
                onSelect={setPeriod}
                focus={lineFocus}
                onFocus={setLineFocus}
              />
            ))}
          </div>

          <div>
            <SubTitle>요약</SubTitle>
            <SummaryTable d={d} focus={lineFocus} />
          </div>

          <Highlights d={d} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...chartBox, flex: '1 1 300px', minWidth: 0 }}>
              <SubTitle>
                조치 현황 ({d.period.label} 이슈 {d.period.total}건)
              </SubTitle>
              <HorizontalBars
                items={REMEDIATION_ORDER.map((st) => ({
                  key: st,
                  label: REMEDIATION_STATUS_LABELS[st],
                  count: d.statusCounts[st] ?? 0,
                  color: REMEDIATION_TONE[st].bar,
                  onClick: () =>
                    navigate(`/field/fault-remediations?status=${st}&issueHalf=${d.period.key}`),
                }))}
              />
            </div>
            <div style={{ ...chartBox, flex: '1 1 300px', minWidth: 0 }}>
              <SubTitle>반복 발생 매장 ({d.sameHalfLastMonth.label}부터 2건 이상)</SubTitle>
              {d.repeatStores.length === 0 ? (
                <div style={SHARED_EMPTY_HINT}>없습니다.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {d.repeatStores.map((s) => (
                    <Link
                      key={s.storeName}
                      to={`/field/fault-remediations?search=${encodeURIComponent(s.storeName)}`}
                      style={storeChip}
                      title="이 매장의 조치 이력 보기"
                    >
                      {s.storeName} <b>{s.count}</b>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <IssueSections issues={d.issues} />
        </div>
      )}
    </Section>
  );
}

function SubTitle({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px', ...style }}>{children}</div>
  );
}

/** 대비 — 늘면 위 화살(빨강), 줄면 아래 화살(파랑). 이슈는 느는 게 나쁜 쪽이다(리포트와 같은 색). */
function Diff({ now, before }: { now: number; before: number }) {
  const diff = now - before;
  if (diff === 0) return <span style={{ color: 'var(--fg-muted)' }}>0건</span>;
  const up = diff > 0;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: up ? 'var(--fg-danger)' : 'var(--fg-brand)',
        fontWeight: 600,
        verticalAlign: 'middle',
      }}
    >
      <Icon name={up ? 'chev-up' : 'chev-down'} size={12} aria-hidden />
      {Math.abs(diff)}건
    </span>
  );
}

/** 변화율 — 리포트처럼 소수 한 자리. 앞 기간이 0건이면 셀 수 없다. */
function Rate({ now, before }: { now: number; before: number }) {
  if (before === 0) return <span style={{ color: 'var(--fg-muted)' }}>–</span>;
  const r = ((now - before) / before) * 100;
  const color = r > 0 ? 'var(--fg-danger)' : r < 0 ? 'var(--fg-brand)' : 'var(--fg-muted)';
  return (
    <span style={{ color }}>
      {r > 0 ? '+' : ''}
      {r.toFixed(1)}%
    </span>
  );
}

function HeadlineTiles({ d }: { d: FieldIssueSummaryResponse }) {
  const tiles: { key: keyof IssueCategoryCount; title: string; color?: string }[] = [
    { key: 'total', title: '전체 이슈' },
    { key: 'robot', title: ISSUE_CATEGORY_LABELS.ROBOT, color: ROBOT_COLOR },
    { key: 'nonRobot', title: ISSUE_CATEGORY_LABELS.NON_ROBOT, color: NON_ROBOT_COLOR },
  ];
  const done = d.statusCounts.COMPLETED ?? 0;
  const open = d.period.total - done;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
      {tiles.map((t) => (
        <div key={t.key} style={tile}>
          <div style={tileTitle}>
            {t.color && <span style={{ ...dot, background: t.color }} aria-hidden />}
            {t.title}
          </div>
          <div style={tileValue}>
            {d.period[t.key]}
            <span style={tileUnit}>건</span>
          </div>
          <div style={tileSub}>
            {d.prev.label} 대비 <Diff now={d.period[t.key]} before={d.prev[t.key]} />
          </div>
          <div style={tileSub}>
            {d.sameHalfLastMonth.label} 대비{' '}
            <Diff now={d.period[t.key]} before={d.sameHalfLastMonth[t.key]} />
          </div>
        </div>
      ))}
      <div style={tile}>
        <div style={tileTitle}>조치 현황</div>
        <div style={tileValue}>
          {done} / {d.period.total}
          <span style={tileUnit}>건 완료</span>
        </div>
        <div style={tileSub}>
          {open > 0 ? (
            <span style={{ color: 'var(--dday-warning)', fontWeight: 600 }}>{open}건 남음</span>
          ) : (
            '모두 완료'
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 15일 단위 추이 — 리포트 그래프처럼 꺾은선. 고른 기간을 그래프 위에 따로 칠하지 않는다(2026-10-27 오너
 * "필드 대시보드에선 이거 빼라고").
 *
 * 선 고르기(2026-10-28 오너 "꺾은선을 클릭하면 그 선에 대한 정보 위주로") — 선이나 범례를 누르면 그 선이
 * 지도처럼 살짝 떠오르고(그림자를 깔고 위로) 나머지 선은 흐려진다. 위에 그 선의 숫자를 적고, 말풍선도 그 선만
 * 보인다. 빈 곳을 누르면 풀린다. 고른 선의 점을 한 번 더 누르면 그 기간으로 바꾼다.
 */
interface TrendChartProps {
  trend: IssueTrendPoint[];
  lines: { key: string; name: string; color: string }[];
  height: number;
  periodKey: string;
  onSelect: (key: string) => void;
  /** 고른 선 — 섹션이 쥔다(요약 표도 같은 줄을 칠한다). 이 그래프에 없는 선이면 아무것도 안 고른 것처럼 그린다. */
  focus: string | null;
  onFocus: (key: string | null) => void;
}

/**
 * 그래프 칸 — 테두리, 제목, 오른쪽 위 '크게 보기'(2026-10-28 오너 "표 확대 기능"). 크게 본 창의 그래프도 선 고르기가
 * 그대로 된다.
 */
function ChartPanel({
  title,
  style,
  ...chart
}: TrendChartProps & { title: string; style?: CSSProperties }) {
  const [big, setBig] = useState(false);
  return (
    <div style={{ ...chartBox, ...style }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <SubTitle>{title}</SubTitle>
        <button
          type="button"
          onClick={() => setBig(true)}
          style={expandBtn}
          aria-label={`${title} 크게 보기`}
        >
          <Icon name="search" size={12} aria-hidden />
          크게 보기
        </button>
      </div>
      <TrendChart {...chart} />
      <Modal open={big} onClose={() => setBig(false)} title={title} width={1200}>
        <TrendChart {...chart} height={Math.max(360, Math.round(window.innerHeight * 0.6))} />
      </Modal>
    </div>
  );
}

function TrendChart({
  trend,
  lines,
  height,
  periodKey,
  onSelect,
  focus: sharedFocus,
  onFocus: setFocus,
}: TrendChartProps) {
  const focus = lines.some((l) => l.key === sharedFocus) ? sharedFocus : null;
  // 그래프 바깥(페이지 어디든)을 누르면 선택을 푼다(2026-10-28 오너 "표 외부 영역을 클릭해도 비활성화") — 섹션이
  // `data-trend-lines` 로 이 그래프 안인지 가린다.
  // 고른 선을 한 번 더 누른 클릭이면 그래프 클릭(기간 고르기)이 이어받는다.
  const pickPeriod = useRef(false);
  // 'GEN1.robot' 같은 열쇠는 모델별 칸에서 꺼낸다.
  const valueOf = (t: IssueTrendPoint, key: string): number => {
    const [model, cat] = key.split('.') as [IssueModelGroup, keyof IssueCategoryCount | undefined];
    return cat ? t.byModel[model][cat] : t[key as keyof IssueCategoryCount];
  };
  const data = trend.map((t) => {
    const row: Record<string, string | number> = { x: axisLabel(t), key: t.key };
    for (const l of lines) row[l.key] = valueOf(t, l.key);
    return row;
  });
  const focused = lines.find((l) => l.key === focus);
  const lift = 4;
  return (
    <div data-trend-lines={lines.map((l) => l.key).join(' ')} style={{ width: '100%' }}>
      <style>{`
        @keyframes kuun-line-rise { from { transform: translate(0, 0); } to { transform: translate(0, -${lift}px); } }
        @keyframes kuun-line-shadow { from { opacity: 0; } to { opacity: 1; } }
        .kuun-trend .recharts-line { transition: opacity 0.2s, filter 0.2s; }
        .kuun-trend .is-dim { opacity: 0.35; filter: blur(1.2px); }
        .kuun-trend .is-on { animation: kuun-line-rise 0.25s ease-out both; }
        .kuun-trend .is-shadow { filter: blur(3px); animation: kuun-line-shadow 0.25s ease-out both; }
        .kuun-trend .is-hit path { cursor: pointer; }
        .kuun-trend :focus { outline: none; }
      `}</style>
      <FocusSummary
        line={focused}
        trend={trend}
        periodKey={periodKey}
        valueOf={valueOf}
        height={22}
      />
      <div
        className="kuun-trend"
        style={{ width: '100%', height }}
        // 누른 곳이 선(굵은 투명 선)인지 범례인지 DOM 에서 가린다 — 그 밖은 빈 곳이라 선택을 푼다.
        onClickCapture={(e) => {
          const el = e.target as Element;
          const hitEl = el.closest('.is-hit');
          const legendEl = el.closest('.recharts-legend-item');
          const idx = hitEl
            ? Number(/hit-(\d+)/.exec(hitEl.getAttribute('class') ?? '')?.[1])
            : lines.findIndex((l) => l.name === legendEl?.textContent?.trim());
          const key = lines[idx]?.key ?? null;
          pickPeriod.current = !!hitEl && key === focus;
          if (!key) setFocus(null);
          else if (key !== focus) setFocus(key);
          else if (legendEl) setFocus(null);
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 12, bottom: 0, left: -12 }}
            onClick={(e) => {
              if (!pickPeriod.current) return;
              pickPeriod.current = false;
              const i = Number(e?.activeTooltipIndex);
              const t = Number.isInteger(i) ? trend[i] : undefined;
              if (t) onSelect(t.key);
            }}
          >
            <CartesianGrid stroke="var(--border-default)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="x"
              tick={{ fontSize: 10, fill: 'var(--fg-muted)' }}
              interval="preserveStartEnd"
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: 'var(--fg-muted)' }}
              width={36}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                // 그림자 · 누르기용 투명 선도 같은 값을 실어 온다 — 진짜 선 이름만, 한 번씩.
                const seen = new Set<string>();
                const items = payload.filter((p) => {
                  const k = String(p.dataKey);
                  const real = lines.some((l) => l.key === k && l.name === p.name);
                  if (!real || seen.has(k) || (focus && k !== focus)) return false;
                  seen.add(k);
                  return true;
                });
                return (
                  <div style={tooltipBox}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
                    {items.map((p) => (
                      <div key={String(p.dataKey)} style={{ color: p.color }}>
                        {p.name} {String(p.value)}건
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Legend
              // 기본은 이름순(로봇 외 · 로봇 · 전체)이라 거꾸로 보였다 — 선을 넘긴 순서 그대로.
              itemSorter={null}
              wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
              iconSize={10}
              formatter={(value, entry) => (
                <span style={{ opacity: focus && entry.dataKey !== focus ? 0.4 : 1 }}>{value}</span>
              )}
            />
            {/* 고른 선의 그림자 — 제자리에 흐리게 깔린다. */}
            {focused && (
              <Line
                key={`_shadow-${focused.key}`}
                type="linear"
                dataKey={focused.key}
                stroke="rgba(0,0,0,0.35)"
                strokeWidth={3}
                dot={false}
                activeDot={false}
                legendType="none"
                tooltipType="none"
                className="is-shadow"
                isAnimationActive={false}
              />
            )}
            {lines.map((l) => {
              // 점은 선과 다른 층에 그려진다(recharts 3) — 점에도 같은 이름표를 달아 함께 뜨고 흐려지게.
              const cls = focus === l.key ? 'is-on' : focus ? 'is-dim' : undefined;
              return (
                <Line
                  key={l.key}
                  type="linear"
                  dataKey={l.key}
                  name={l.name}
                  stroke={l.color}
                  strokeWidth={focus === l.key ? 3 : 2}
                  dot={{ r: focus === l.key ? 4 : 3, className: cls }}
                  className={cls}
                  isAnimationActive={false}
                />
              );
            })}
            {/* 누르기 쉬운 굵은 투명 선 — 가는 선을 정확히 누르지 않아도 된다. 고른 선이 맨 위. */}
            {lines
              .map((l, i) => ({ l, i }))
              .sort((a, b) => Number(a.l.key === focus) - Number(b.l.key === focus))
              .map(({ l, i }) => (
                <Line
                  key={`_hit-${l.key}`}
                  type="linear"
                  dataKey={l.key}
                  stroke="transparent"
                  strokeWidth={14}
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  tooltipType="none"
                  className={`is-hit hit-${i}`}
                  isAnimationActive={false}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** 고른 선의 숫자 — 고른 기간 건수, 1년 평균, 가장 많았던 때. 고른 선이 없으면 안내 한 줄. */
function FocusSummary({
  line,
  trend,
  periodKey,
  valueOf,
  height,
}: {
  line: { key: string; name: string; color: string } | undefined;
  trend: IssueTrendPoint[];
  periodKey: string;
  valueOf: (t: IssueTrendPoint, key: string) => number;
  height: number;
}) {
  const box: CSSProperties = {
    minHeight: height,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '2px 12px',
    fontSize: 12,
    color: 'var(--fg-muted)',
    marginBottom: 4,
  };
  if (!line) return <div style={box}>선을 누르면 그 선의 숫자만 모아 봅니다.</div>;
  const values = trend.map((t) => valueOf(t, line.key));
  const cur = trend.find((t) => t.key === periodKey);
  const peakIdx = values.indexOf(Math.max(...values));
  const peak = trend[peakIdx];
  const avg = values.reduce((a, b) => a + b, 0) / (values.length || 1);
  return (
    <div style={box}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
        <span style={{ ...dot, background: line.color }} aria-hidden />
        <span style={{ color: 'var(--fg-default)' }}>{line.name}</span>
      </span>
      {cur && (
        <span>
          {cur.key.slice(0, 4)}년 {cur.label}{' '}
          <b style={{ color: 'var(--fg-default)' }}>{valueOf(cur, line.key)}건</b>
        </span>
      )}
      <span>1년 평균 {avg.toFixed(1)}건</span>
      {peak && (
        <span>
          가장 많았던 때 {peak.key.slice(0, 4)}년 {peak.label} {values[peakIdx]}건
        </span>
      )}
    </div>
  );
}

/** '2026-10-1' → '26.10 상반기' */
function shortHalf(key: string): string {
  return `${key.slice(2, 4)}.${key.slice(5, 7)} ${key.endsWith('-1') ? '상반기' : '하반기'}`;
}

/** 그래프 아래 글자 — 리포트처럼 '1.1-15', '1.16-31'. */
function axisLabel(t: IssueHalfMonthCount): string {
  return t.days.replace('~', '-');
}

/**
 * 요약 — 리포트 표와 같은 칸(앞 두 기간 · 이번 기간 · 대비 · 변화율). 모델마다 합계 · 로봇 이슈 · 로봇 외 이슈를
 * 나눈다(2026-10-27 오너 — "gen1의 로봇 이슈, 로봇 외 이슈, gen2의 로봇 이슈, 로봇 외 이슈 이런식으로 나눠야").
 */
function SummaryTable({ d, focus }: { d: FieldIssueSummaryResponse; focus: string | null }) {
  // 그래프에서 고른 선의 줄 — 'robot' 은 전체의 로봇 이슈, 'GEN1.robot' 은 GEN1 의 로봇 이슈.
  const [focusModel, focusLine] = focus?.includes('.')
    ? (focus.split('.') as [string, keyof IssueCategoryCount])
    : ['전체', focus as keyof IssueCategoryCount | null];
  const focusColor = !focus
    ? undefined
    : focus.includes('.')
      ? MODEL_COLOR[focusModel as keyof typeof MODEL_COLOR]
      : focus === 'total'
        ? TOTAL_COLOR
        : focus === 'robot'
          ? ROBOT_COLOR
          : NON_ROBOT_COLOR;
  const groups: {
    name: string;
    cur: IssueCategoryCount;
    prev: IssueCategoryCount;
    last: IssueCategoryCount;
  }[] = [
    { name: '전체', cur: d.period, prev: d.prev, last: d.sameHalfLastMonth },
    ...d.byModel.map((m) => ({
      name: m.model,
      cur: m.current,
      prev: m.prev,
      last: m.sameHalfLastMonth,
    })),
  ];
  const lines: { key: keyof IssueCategoryCount; label: string }[] = [
    { key: 'total', label: '합계' },
    { key: 'robot', label: ISSUE_CATEGORY_LABELS.ROBOT },
    { key: 'nonRobot', label: ISSUE_CATEGORY_LABELS.NON_ROBOT },
  ];
  const head = (c: IssueHalfMonthCount) => (
    <>
      {c.days}
      <br />
      <span style={{ fontWeight: 400 }}>({c.label})</span>
    </>
  );
  return (
    <div style={{ ...tableBox, overflowX: 'auto' }} data-keep-line-focus="">
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: 'hidden' }}
        data-plain-table=""
      >
        <thead>
          <tr>
            <th style={th}>모델</th>
            <th style={th}>구분</th>
            {/* 기간 머리글 사이 세로선은 연하게(2026-10-28 오너) — 겹친 선은 왼쪽 칸 것이 이겨 오른쪽 선에 긋는다. */}
            <th style={{ ...thNum, borderRight: CELL_BORDER }}>{head(d.sameHalfLastMonth)}</th>
            <th style={{ ...thNum, borderRight: CELL_BORDER }}>{head(d.prev)}</th>
            <th style={thNum}>{head(d.period)}</th>
            {/* 머리글: '대비' 앞은 진하게, '대비'와 '변화율' 사이는 연하게(2026-10-28 오너 빨강 · 파랑 표시). 겹친 선은
                왼쪽 칸 것이 이겨서 연한 선은 '대비' 칸의 오른쪽에 긋는다. */}
            <th style={{ ...thNum, borderLeft: GROUP_LINE, borderRight: CELL_BORDER }}>
              {d.prev.label} 대비
            </th>
            <th style={thNum}>변화율</th>
            <th style={{ ...thNum, borderLeft: GROUP_LINE, borderRight: CELL_BORDER }}>
              {d.sameHalfLastMonth.label} 대비
            </th>
            <th style={thNum}>변화율</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((g, gi) =>
            lines.map((l, i) => {
              const sum = l.key === 'total';
              // 전체 · GEN1 · GEN2 … 묶음 사이 진한 선(2026-10-28 오너 "파트별로 가로선을 좀 더 진하게"). 줄(tr)이 아니라
              // 칸마다 긋는다 — 겹친 선에서는 칸 선이 줄 선을 이겨, 칸의 연한 위 선이 덮었다.
              const lit = !!focus && g.name === focusModel && l.key === focusLine;
              const top: CSSProperties | undefined =
                i === 0 && gi > 0 ? { borderTop: GROUP_LINE } : undefined;
              return (
                <tr
                  key={`${g.name}-${l.key}`}
                  data-testid={lit ? 'issue-summary-lit-row' : undefined}
                  style={{
                    fontWeight: sum ? 700 : undefined,
                    // 모델 칸(rowSpan)은 묶음 머리라 칠하지 않는다 — 칠한 줄의 칸들만 그 선 색으로 옅게.
                    background: lit
                      ? `color-mix(in srgb, ${focusColor} 14%, transparent)`
                      : undefined,
                    transition: 'background 0.2s',
                  }}
                >
                  {i === 0 && (
                    <td
                      rowSpan={lines.length}
                      style={{ ...td, ...top, whiteSpace: 'nowrap', fontWeight: 700 }}
                    >
                      {g.name}
                    </td>
                  )}
                  <td
                    style={{
                      ...td,
                      ...top,
                      borderLeft: GROUP_LINE,
                      whiteSpace: 'nowrap',
                      color: l.key === 'nonRobot' ? NON_ROBOT_TEXT : undefined,
                    }}
                  >
                    {l.label}
                  </td>
                  <td style={{ ...tdNum, ...top, borderLeft: GROUP_LINE }}>{g.last[l.key]}</td>
                  <td style={{ ...tdNum, ...top }}>{g.prev[l.key]}</td>
                  <td style={{ ...tdNum, ...top, fontWeight: 700 }}>{g.cur[l.key]}</td>
                  <td style={{ ...tdNum, ...top, borderLeft: GROUP_LINE }}>
                    <Diff now={g.cur[l.key]} before={g.prev[l.key]} />
                  </td>
                  <td style={{ ...tdNum, ...top }}>
                    <Rate now={g.cur[l.key]} before={g.prev[l.key]} />
                  </td>
                  <td style={{ ...tdNum, ...top, borderLeft: GROUP_LINE }}>
                    <Diff now={g.cur[l.key]} before={g.last[l.key]} />
                  </td>
                  <td style={{ ...tdNum, ...top }}>
                    <Rate now={g.cur[l.key]} before={g.last[l.key]} />
                  </td>
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );
}

type Issue = FieldIssueSummaryResponse['issues'][number];

/**
 * 신규 이슈 · 주요 누적 이슈 — 리포트 2 · 3번 글. 사람이 쓰던 글을 유형으로 대신한다(서버가 주된 유형으로 가름):
 * 앞선 석 달 그 모델에 없던 유형이면 신규, 있던 유형이면 누적. 칸마다 오른쪽 위 펜으로 글을 고칠 수 있다.
 */
function Highlights({ d }: { d: FieldIssueSummaryResponse }) {
  const byId = new Map(d.issues.map((i) => [i.id, i]));
  const fresh = d.highlights
    .filter((h) => h.newIssueIds.length > 0)
    .map((h) => ({
      model: h.model,
      lines: h.newIssueIds.flatMap((id) => {
        const i = byId.get(id);
        return i ? [freshLine(i)] : [];
      }),
    }));
  const recurring = d.highlights
    .filter((h) => h.recurring.length > 0)
    .map((h) => ({
      model: h.model,
      count: h.recurring.reduce((n, r) => n + r.count, 0),
      lines: h.recurring.map((r) => ({
        type: r.type,
        // 2026-10-28 오너 "자동으로 생성되는 문장이 상당히 어색한데" — 건수를 문장 안으로 넣는다.
        text:
          r.streak >= 2
            ? `${r.since}부터 반기마다 이어서 발생하고 있습니다. 이번에 ${r.count}건, 그 전 석 달 동안 ${r.recentCount}건 있었습니다.`
            : `지난 석 달 동안 ${r.recentCount}건 있었는데 이번에 ${r.count}건 다시 발생했습니다.`,
        muted: r.storeNames.length > 0 ? `매장: ${r.storeNames.join(', ')}` : undefined,
      })),
    }));
  // 펜을 누르면 이 글이 먼저 들어 있다 — 화면과 같은 줄 모양(NoteBody 가 다시 읽는다).
  const asText = (groups: { model: string; head: string; lines: HighlightLineProps[] }[]) =>
    groups
      .map(
        (g) =>
          `${g.head}\n` +
          g.lines.map((l) => `• [${l.type}] ${l.text}${l.muted ? ` ${l.muted}` : ''}`).join('\n'),
      )
      .join('\n\n');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
      <NoteBox
        key={`NEW-${d.period.key}`}
        title="신규 이슈"
        section="NEW"
        d={d}
        autoText={asText(fresh.map((g) => ({ ...g, head: `${g.model} (${g.lines.length}건)` })))}
      >
        {fresh.length === 0 ? (
          <div style={SHARED_EMPTY_HINT}>앞선 석 달에 없던 유형의 이슈는 없습니다.</div>
        ) : (
          fresh.map((g) => (
            <ModelBlock key={g.model} model={g.model} head={`${g.model} (${g.lines.length}건)`}>
              {g.lines.map((l, k) => (
                <HighlightLine key={k} {...l} />
              ))}
            </ModelBlock>
          ))
        )}
      </NoteBox>
      <NoteBox
        key={`RECURRING-${d.period.key}`}
        title="주요 누적 이슈"
        section="RECURRING"
        d={d}
        autoText={asText(recurring.map((g) => ({ ...g, head: `${g.model} (${g.count}건)` })))}
      >
        {recurring.length === 0 ? (
          <div style={SHARED_EMPTY_HINT}>앞선 석 달에 이어서 난 유형은 없습니다.</div>
        ) : (
          recurring.map((g) => (
            <ModelBlock key={g.model} model={g.model} head={`${g.model} (${g.count}건)`}>
              {g.lines.map((l) => (
                <HighlightLine key={l.type} {...l} />
              ))}
            </ModelBlock>
          ))
        )}
      </NoteBox>
    </div>
  );
}

/** 신규 이슈 한 줄 — 첫 줄만, 노션 글머리('증상 :' · '해결 :' · '현황 및 해결 :' · '•')는 뗀다. */
function freshLine(i: Issue): HighlightLineProps {
  const first = (t: string | null) => t?.split('\n')[0]?.replace(ISSUE_LEAD, '').trim();
  const cause = first(i.cause);
  const action = first(i.action);
  return {
    type: i.types.join(' · '),
    text: `${i.storeName ?? '매장 미상'}에서 ${i.title}${cause ? ` — ${cause}` : ''}`,
    muted: action ? `조치: ${action}` : undefined,
  };
}

function ModelBlock({
  model,
  head,
  children,
}: {
  model: string;
  head: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={modelHead}>
        <span
          style={{
            ...dot,
            background: MODEL_COLOR[model as IssueModelGroup] ?? MODEL_COLOR.기타,
          }}
          aria-hidden
        />
        {head}
      </div>
      <ul style={bulletList}>{children}</ul>
    </div>
  );
}

type HighlightLineProps = { type?: string; text: string; muted?: string };

function HighlightLine({ type, text, muted }: HighlightLineProps) {
  return (
    <li style={{ marginBottom: 4 }}>
      {type && <span style={typeChip}>{type}</span>} {text}
      {muted && <span style={{ color: 'var(--fg-muted)' }}> {muted}</span>}
    </li>
  );
}

/**
 * 고쳐 둔 글 — 자동 글과 같은 모양으로 보이게 줄을 읽는다: 모델 이름으로 시작하는 줄은 머리, '•' · '-' 줄은 항목,
 * '[유형]' 으로 시작하면 칩.
 */
function NoteBody({ body }: { body: string }) {
  const blocks: { head: string | null; lines: string[] }[] = [];
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const isHead = /^(GEN1|GEN2|ZEPHYR|기타)(\s|\(|$)/.test(line);
    if (isHead || blocks.length === 0) blocks.push({ head: isHead ? line : null, lines: [] });
    if (!isHead) blocks[blocks.length - 1].lines.push(line);
  }
  return (
    <>
      {blocks.map((b, k) => {
        const items = b.lines.map((l, j) => {
          const m = /^(?:[•-]\s*)?(?:\[([^\]]+)\]\s*)?(.*)$/.exec(l);
          // 끝의 '조치: …' · '매장: …' 은 자동 글처럼 옅게
          const [, text, muted] = /^(.*?)\s*((?:조치|매장):.*)?$/.exec(m?.[2] ?? l) ?? [];
          return <HighlightLine key={j} type={m?.[1]} text={text ?? l} muted={muted} />;
        });
        return b.head ? (
          <ModelBlock key={k} model={b.head.split(/[\s(]/)[0]} head={b.head}>
            {items}
          </ModelBlock>
        ) : (
          <ul key={k} style={{ ...bulletList, marginBottom: 10 }}>
            {items}
          </ul>
        );
      })}
    </>
  );
}

/**
 * 글 칸 하나 — 오른쪽 위 펜(2026-10-28 오너 "수정 버튼 하나")으로 고친다. 처음 열면 자동 글이 들어 있고,
 * 고친 글은 그 반기에만 남는다. 다 지우고 저장하거나 '자동 글로'를 누르면 자동 글로 돌아간다.
 */
function NoteBox({
  title,
  section,
  d,
  autoText,
  children,
}: {
  title: string;
  section: FieldIssueNoteSection;
  d: FieldIssueSummaryResponse;
  autoText: string;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const canEdit = !!user && canEditAny(user.role);
  const qc = useQueryClient();
  const toast = useToast();
  const note = d.notes?.[section];
  // null 이면 보는 중, 문자열이면 고치는 중
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: string) =>
      teamDashboardApi.setFieldIssueNote({ period: d.period.key, section, body }),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['dashboard', 'field', 'issue-summary'] });
      setDraft(null);
      toast.success(saved ? `${title} 글을 고쳤습니다.` : `${title}를 자동 글로 되돌렸습니다.`);
    },
    onError: (err) => toast.error(extractApiError(err), `${title} 저장 실패`),
  });
  return (
    <div style={{ ...chartBox, flex: '1 1 360px', minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <SubTitle>
          {title}
          {note && (
            <span
              style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--fg-muted)' }}
            >
              {note.updatedByName ?? '알 수 없음'} 고침 · {kstYmd(note.updatedAt)}
            </span>
          )}
        </SubTitle>
        {canEdit && draft === null && (
          <button
            type="button"
            onClick={() => setDraft(note?.body ?? autoText)}
            style={penBtn}
            aria-label={`${title} 고치기`}
            title="고치기"
          >
            <Icon name="pen" size={13} aria-hidden />
          </button>
        )}
      </div>
      {draft !== null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Textarea
            aria-label={`${title} 글`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ minHeight: 220, fontSize: 13, lineHeight: 1.6 }}
            autoFocus
          />
          <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            모델 이름으로 시작하는 줄은 제목, • 로 시작하는 줄은 항목, [유형] 은 칩으로 보입니다.
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            {note && (
              <Button
                variant="ghost"
                onClick={() => save.mutate('')}
                disabled={save.isPending}
                style={{ marginRight: 'auto' }}
              >
                자동 글로
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDraft(null)} disabled={save.isPending}>
              취소
            </Button>
            <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
              저장
            </Button>
          </div>
        </div>
      ) : note ? (
        <NoteBody body={note.body} />
      ) : (
        children
      )}
    </div>
  );
}

/** 건별 표 — 리포트처럼 로봇 이슈 / 로봇 외 이슈로 먼저 나누고, 그 안에서 모델별 표. */
function IssueSections({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return <div style={SHARED_EMPTY_HINT}>이 기간에는 이슈가 없습니다.</div>;
  const cats: IssueCategory[] = ['ROBOT', 'NON_ROBOT'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {cats.map((cat) => {
        const inCat = issues.filter((i) => i.issueCategory === cat);
        if (inCat.length === 0) return null;
        const models = [...new Set(inCat.map((i) => i.model))];
        return (
          <div key={cat}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 15,
                fontWeight: 700,
                marginBottom: 10,
                color: CATEGORY_TEXT[cat],
              }}
            >
              <span style={{ ...categoryBar, background: CATEGORY_TEXT[cat] }} aria-hidden />
              {ISSUE_CATEGORY_LABELS[cat]} ({inCat.length}건)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {models.map((m) => {
                const list = inCat.filter((i) => i.model === m);
                return (
                  <div key={m}>
                    <div style={modelHead}>
                      <span style={{ ...dot, background: MODEL_COLOR[m] }} aria-hidden />
                      {m}
                      <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>
                        <span style={{ color: CATEGORY_TEXT[cat], fontWeight: 600 }}>
                          {cat === 'ROBOT' ? '로봇 관련 이슈' : '로봇 외 이슈'}
                        </span>{' '}
                        ({list.length}건)
                      </span>
                    </div>
                    <IssueTable issues={list} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IssueTable({ issues }: { issues: Issue[] }) {
  return (
    <div style={{ ...tableBox, overflowX: 'auto' }}>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: 'hidden' }}
        data-plain-table=""
      >
        <thead>
          <tr>
            <th style={{ ...thNum, ...thSoftSides }}>No.</th>
            <th style={{ ...th, ...thSoftSides }}>이슈번호</th>
            <th style={{ ...th, ...thSoftSides }}>발생일</th>
            <th style={{ ...th, ...thSoftSides }}>이슈 유형</th>
            <th style={{ ...th, ...thSoftSides }}>발생 매장</th>
            <th style={{ ...th, ...thSoftSides }}>이슈 내용</th>
            <th style={{ ...th, ...thSoftSides }}>증상 / 원인</th>
            <th style={{ ...th, ...thSoftSides }}>조치 내용</th>
            <th style={{ ...th, ...thSoftSides }}>상태</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((i, idx) => {
            const tone = REMEDIATION_TONE[i.status as RemediationStatus];
            const ymd = kstYmd(i.occurredAt);
            return (
              <tr key={i.id}>
                <td style={tdNum}>{idx + 1}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {i.reportNo ? (
                    <Link
                      to={`/field/fault-remediations?search=${encodeURIComponent(i.reportNo)}`}
                      style={{ color: 'var(--fg-brand)', textDecoration: 'none' }}
                    >
                      {i.reportNo}
                    </Link>
                  ) : (
                    '–'
                  )}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {ymd ? ymd.split('.').slice(1).map(Number).join('/') : '–'}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{i.types.join(' · ')}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{i.storeName ?? '–'}</td>
                <td style={{ ...td, minWidth: 160 }}>{i.title}</td>
                <td style={{ ...td, minWidth: 200, whiteSpace: 'pre-line' }}>{i.cause ?? '–'}</td>
                <td style={{ ...td, minWidth: 180, whiteSpace: 'pre-line' }}>{i.action ?? '–'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {tone ? (
                    <span style={{ ...pill, background: tone.bg, color: tone.fg }}>
                      {REMEDIATION_STATUS_LABELS[i.status]}
                    </span>
                  ) : (
                    i.status
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const selectStyle: CSSProperties = {
  height: 30,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
const tile: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--border-card)',
  background: 'var(--bg-surface)',
};
const tileTitle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--fg-muted)',
};
const tileValue: CSSProperties = { fontSize: 24, fontWeight: 700, margin: '2px 0' };
const tileUnit: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--fg-muted)',
  marginLeft: 3,
};
const tileSub: CSSProperties = { fontSize: 11, color: 'var(--fg-muted)', lineHeight: 1.6 };
const dot: CSSProperties = { width: 8, height: 8, borderRadius: 2, display: 'inline-block' };
const modelHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 6,
};
const bulletList: CSSProperties = { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 };
const typeChip: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 4,
  background: 'var(--bg-subtle)',
  color: 'var(--fg-default)',
  whiteSpace: 'nowrap',
};
/** 노션 글머리 — '해결 :', '증상 및 원인 :', '현황 및 해결 :', '현황/해결:' 처럼 낱말이 둘 이상 이어져도 뗀다. */
const LEAD_WORD = '(?:증상|원인|해결|현황|조치)';
const ISSUE_LEAD = new RegExp(
  `^\\s*[•-]?\\s*(?:${LEAD_WORD}(?:\\s*(?:및|/|·|,)\\s*${LEAD_WORD})*\\s*:\\s*)?`,
);
const tooltipBox: CSSProperties = {
  fontSize: 12,
  padding: '6px 8px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 4,
};
// 상자 테두리는 진하게, 표 안 칸 선은 연하게(2026-10-28 오너 "진하기는 블럭 테두리에만").
const BOX_BORDER = '1px solid var(--border-strong)';
const CELL_BORDER = '1px solid var(--border-subtle)';
// 묶음 사이 굵은 선 — 가로는 전체 · GEN1 · GEN2 사이, 세로는 대비 · 변화율 두 쌍 앞(2026-10-28 오너).
// 2026-10-28 오너 "두껍게 한 선들은 그냥 이정도 느낌으로" · "이런 선을 구분선으로" — 머리글 칸 선과 같은 1px 진한 선.
const GROUP_LINE = BOX_BORDER;
const expandBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  height: 24,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  fontSize: 11,
  cursor: 'pointer',
};
/** 신규 · 누적 이슈 칸 오른쪽 위 펜 — 크게 보기 버튼과 같은 틀, 아이콘만. */
const penBtn: CSSProperties = { ...expandBtn, width: 24, padding: 0, justifyContent: 'center' };
const categoryBar: CSSProperties = {
  width: 4,
  height: 16,
  borderRadius: 2,
  display: 'inline-block',
};
/** 그래프마다 테두리 — 붙어 있는 그래프가 서로 구분되게(2026-10-28 오너 "테두리를 그리라고"). */
const chartBox: CSSProperties = { border: BOX_BORDER, borderRadius: 6, padding: '12px 12px 8px' };
const tableBox: CSSProperties = { border: BOX_BORDER, borderRadius: 6, overflow: 'hidden' };
const th: CSSProperties = {
  padding: '7px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  // 머리글 칸은 진하게 그대로(2026-10-28 오너 "헤더들은 제외")
  border: BOX_BORDER,
};
const thNum: CSSProperties = { ...th, textAlign: 'right' };
/** 건별 표 머리글 — 칸 사이 세로선과 아래 가로선 모두 연한 선(2026-10-28 오너 "그냥 연한 선 긋자"). */
const thSoftSides: CSSProperties = {
  borderLeft: CELL_BORDER,
  borderRight: CELL_BORDER,
  borderBottom: CELL_BORDER,
};
// 본문 칸은 왼쪽 · 위 선만 — 겹친 선은 같은 굵기면 왼쪽 · 위 칸 것이 이겨서, 네 변을 다 그으면 연한 선이 진한
// 구분선(GROUP_LINE)을 덮었다. 표 바깥은 tableBox 가 긋는다.
const td: CSSProperties = { padding: '7px 10px', borderLeft: CELL_BORDER, borderTop: CELL_BORDER };
const tdNum: CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const storeChip: CSSProperties = {
  fontSize: 12,
  padding: '3px 8px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-default)',
  textDecoration: 'none',
};
